/**
 * parlayCustom.js — pure logic for the Parlay Builder's "Build Your
 * Own" mode: selection state (one leg per game, 2-7 legs) and slip
 * math. No imports, no I/O — safe to unit-test in plain node
 * (tests/parlay-custom.test.mjs).
 *
 * The math intentionally mirrors netlify/functions/utils/
 * parlayEngine.js slipMetrics() formula-for-formula:
 *   combined odds = product of leg decimals
 *   true chance   = product of leg fair (de-vigged) probabilities
 *   EV            = trueChance x combinedDecimal - 1
 * so a hand-built slip prices identically to a BettorAI Suggested
 * slip made of the same legs. The test suite proves the equivalence
 * against the engine itself.
 */

export const MIN_CUSTOM_LEGS = 2;
export const MAX_CUSTOM_LEGS = 7;

// Candidate identity matches the builder screen's swap logic: same
// game, market, selection and price is the same leg.
export function sameCandidate(a, b) {
  return Boolean(
    a && b &&
    a.eventId === b.eventId &&
    a.market === b.market &&
    a.selection === b.selection &&
    a.american === b.american
  );
}

export function isCandidateSelected(selectedLegs, candidate) {
  return (selectedLegs || []).some((leg) => sameCandidate(leg, candidate));
}

export function selectedLegForGame(selectedLegs, eventId) {
  return (selectedLegs || []).find((leg) => leg.eventId === eventId) || null;
}

/**
 * Toggle a candidate on the slip, enforcing ONE LEG PER GAME (the
 * engine's rule for built slips, applied to hand-built ones):
 *  - tapping the currently selected leg again removes it;
 *  - tapping another leg from the same game replaces that game's leg
 *    in place (position in the slip preserved);
 *  - tapping a leg from a new game appends it — unless the slip is
 *    already at MAX_CUSTOM_LEGS, in which case the SAME array is
 *    returned unchanged (callers detect the no-op by reference).
 * Replacing a leg is always allowed, even at the cap.
 */
export function toggleLeg(selectedLegs, candidate) {
  const legs = selectedLegs || [];
  const gameIndex = legs.findIndex((leg) => leg.eventId === candidate.eventId);
  if (gameIndex >= 0) {
    if (sameCandidate(legs[gameIndex], candidate)) {
      return legs.filter((_, i) => i !== gameIndex);
    }
    const next = legs.slice();
    next[gameIndex] = candidate;
    return next;
  }
  if (legs.length >= MAX_CUSTOM_LEGS) return legs;
  return [...legs, candidate];
}

export function decimalToAmerican(decimal) {
  if (decimal >= 2) return Math.round((decimal - 1) * 100);
  return Math.round(-100 / (decimal - 1));
}

/**
 * Slip metrics with the engine's exact formulas (see header).
 * combinedAmerican is 0 for an empty slip — the raw conversion would
 * divide by zero there, and the screens have always guarded the
 * empty case this way.
 */
export function customSlipMetrics(legs) {
  const list = legs || [];
  let combinedDecimal = 1;
  let trueChance = 1;
  for (const leg of list) {
    combinedDecimal *= leg.decimal;
    trueChance *= leg.fairProb;
  }
  return {
    combinedDecimal,
    combinedAmerican: list.length ? decimalToAmerican(combinedDecimal) : 0,
    trueChance,
    ev: trueChance * combinedDecimal - 1
  };
}

/** A hand-built slip can be saved at 2-7 legs (a parlay needs 2+). */
export function canSaveCustomSlip(legs) {
  const count = (legs || []).length;
  return count >= MIN_CUSTOM_LEGS && count <= MAX_CUSTOM_LEGS;
}

/**
 * Board search for Build Your Own: keep the games whose team identity
 * contains the query (case-insensitive substring). Game objects from
 * the builder payload carry homeTeam, awayTeam and a matchup string
 * ("Away @ Home") — any of the fields that exist may match. An
 * empty/blank query returns the list unchanged (same array), so the
 * unfiltered board keeps its payload order and identity.
 */
export function filterGamesByQuery(games, query) {
  const list = games || [];
  const q = String(query || '').trim().toLowerCase();
  if (!q) return list;
  return list.filter((game) => {
    if (!game) return false;
    return [game.homeTeam, game.awayTeam, game.matchup].some(
      (field) => typeof field === 'string' && field.toLowerCase().includes(q)
    );
  });
}
